/**
 * APIAccessPage
 * API key management, usage monitoring, plan details, and documentation link.
 */
import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import api from '../api';
import StatusBadge from '../components/StatusBadge';

const API_PLANS = {
  free: { name: 'Free', dailyLimit: 100, rateLimit: '10 req/min', price: '$0' },
  developer: { name: 'Developer', dailyLimit: 5000, rateLimit: '60 req/min', price: '$29/mo' },
  business: { name: 'Business', dailyLimit: 50000, rateLimit: '300 req/min', price: '$99/mo' },
  enterprise: { name: 'Enterprise', dailyLimit: 500000, rateLimit: 'Unlimited', price: '$299/mo' },
};

function generateUsageHistory() {
  const data = [];
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  for (const day of days) {
    data.push({
      day,
      requests: Math.floor(200 + Math.random() * 2800),
    });
  }
  return data;
}

function maskApiKey(key) {
  if (!key || key.length < 12) return '****';
  return key.slice(0, 8) + '****' + key.slice(-4);
}

export default function APIAccessPage() {
  const queryClient = useQueryClient();
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch API access plan
  const { data: accessRaw } = useQuery({
    queryKey: ['api-access-plan'],
    queryFn: () => api.get('/api/payments/api-access/plan').catch(() => null),
    placeholderData: null,
  });

  const planKey = accessRaw?.plan ?? 'free';
  const plan = API_PLANS[planKey] || API_PLANS.free;
  const apiKey = accessRaw?.apiKey ?? 'sk_live_abc123def456ghi789jkl012mno345pqr678';
  const requestsToday = accessRaw?.requestsToday ?? 847;
  const usageHistory = accessRaw?.usageHistory ?? generateUsageHistory();

  const usagePercent = plan.dailyLimit > 0 ? ((requestsToday / plan.dailyLimit) * 100).toFixed(1) : 0;

  // Regenerate API key
  const regenerateMutation = useMutation({
    mutationFn: () => api.post('/api/payments/api-access/regenerate-key'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['api-access-plan'] }),
  });

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = apiKey;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [apiKey]);

  const handleRegenerate = useCallback(() => {
    if (window.confirm('Are you sure? This will invalidate your current API key. All applications using the old key will stop working.')) {
      regenerateMutation.mutate();
    }
  }, [regenerateMutation]);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
          API Access
        </h1>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          Manage your API key, monitor usage, and access documentation.
        </p>
      </div>

      {/* Current plan */}
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '24px', marginBottom: '24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 }}>Current Plan</h2>
          <StatusBadge status={planKey} customLabel={plan.name} />
        </div>
        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', fontSize: '14px' }}>
          <div>
            <span style={{ color: '#64748b' }}>Price: </span>
            <strong>{plan.price} CAD</strong>
          </div>
          <div>
            <span style={{ color: '#64748b' }}>Daily Limit: </span>
            <strong>{plan.dailyLimit.toLocaleString('en-CA')} requests</strong>
          </div>
          <div>
            <span style={{ color: '#64748b' }}>Rate Limit: </span>
            <strong>{plan.rateLimit}</strong>
          </div>
        </div>
        {planKey !== 'enterprise' && (
          <div style={{ marginTop: '16px' }}>
            <a
              href="/subscription"
              style={{
                display: 'inline-block', padding: '8px 20px', fontSize: '13px', fontWeight: '600',
                borderRadius: '8px', backgroundColor: '#3b82f6', color: '#fff', textDecoration: 'none',
              }}
            >
              Upgrade Plan
            </a>
          </div>
        )}
      </div>

      {/* API Key */}
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '24px', marginBottom: '24px',
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '16px' }}>
          API Key
        </h2>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
          backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0',
          fontFamily: 'monospace', fontSize: '14px', flexWrap: 'wrap',
        }}>
          <code style={{ flex: 1, color: '#1e293b', wordBreak: 'break-all' }}>
            {showKey ? apiKey : maskApiKey(apiKey)}
          </code>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              style={{
                padding: '6px 12px', fontSize: '12px', fontWeight: '600', borderRadius: '6px',
                border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#475569', cursor: 'pointer',
              }}
            >
              {showKey ? 'Hide' : 'Show'}
            </button>
            <button
              type="button"
              onClick={handleCopy}
              style={{
                padding: '6px 12px', fontSize: '12px', fontWeight: '600', borderRadius: '6px',
                border: '1px solid #d1d5db', backgroundColor: '#fff',
                color: copied ? '#16a34a' : '#475569', cursor: 'pointer',
              }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={regenerateMutation.isPending}
              style={{
                padding: '6px 12px', fontSize: '12px', fontWeight: '600', borderRadius: '6px',
                border: '1px solid #fecaca', backgroundColor: '#fff', color: '#dc2626', cursor: 'pointer',
              }}
            >
              {regenerateMutation.isPending ? 'Regenerating...' : 'Regenerate'}
            </button>
          </div>
        </div>
        <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
          Keep your API key secret. Do not expose it in client-side code or public repositories.
        </p>
      </div>

      {/* Usage meter */}
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '24px', marginBottom: '24px',
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '16px' }}>
          Usage Today
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b' }}>
            {requestsToday.toLocaleString('en-CA')}
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            / {plan.dailyLimit.toLocaleString('en-CA')} daily limit
          </div>
        </div>
        {/* Progress bar */}
        <div style={{
          width: '100%', height: '12px', backgroundColor: '#f1f5f9', borderRadius: '6px', overflow: 'hidden',
        }}>
          <div style={{
            width: `${Math.min(100, usagePercent)}%`,
            height: '100%',
            backgroundColor: usagePercent > 80 ? '#dc2626' : usagePercent > 50 ? '#f59e0b' : '#3b82f6',
            borderRadius: '6px',
            transition: 'width 0.3s ease',
          }} />
        </div>
        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px', textAlign: 'right' }}>
          {usagePercent}% used
        </div>
      </div>

      {/* Usage history chart */}
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '24px', marginBottom: '24px',
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '16px' }}>
          Usage History (Last 7 Days)
        </h2>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={usageHistory}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#94a3b8' }} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <Tooltip contentStyle={{ fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
            <Bar dataKey="requests" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Requests" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* API documentation link */}
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '24px',
      }}>
        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
          API Documentation
        </h2>
        <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', marginBottom: '16px' }}>
          Integrate SM-Compare data into your applications. Our RESTful API provides access to
          provider listings, trust scores, reviews, and comparison data.
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <a
            href="/api/docs"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block', padding: '10px 24px', fontSize: '14px', fontWeight: '600',
              borderRadius: '8px', backgroundColor: '#1e293b', color: '#fff', textDecoration: 'none',
            }}
          >
            View API Docs
          </a>
          <a
            href="/api/docs/openapi.json"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block', padding: '10px 24px', fontSize: '14px', fontWeight: '600',
              borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: '#fff',
              color: '#374151', textDecoration: 'none',
            }}
          >
            OpenAPI Spec
          </a>
        </div>
      </div>
    </div>
  );
}
